import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  TICKET_STATUS_LABELS, 
  TICKET_STATUS_COLORS,
  TICKET_CATEGORY_LABELS
} from '../services/ticketService';
import TicketDetailModal from './TicketDetailModal';

const TicketListComponent = ({ 
  tickets, 
  totalCount, 
  currentPage, 
  pageSize, 
  onPageChange 
}) => {
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTicketClick = (ticketId) => {
    setSelectedTicketId(ticketId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicketId(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaginationInfo = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalCount);
    
    return {
      totalPages,
      startItem,
      endItem,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1
    };
  };

  if (tickets.length === 0) {
    return (
      <EmptyState>
        <EmptyTitle>暂无工单</EmptyTitle>
        <EmptyDescription>您还没有提交过工单</EmptyDescription>
      </EmptyState>
    );
  }

  return (
    <>
      <TicketsList>
        {tickets.map(ticket => (
          <TicketCard key={ticket.id} onClick={() => handleTicketClick(ticket.id)}>
            <TicketHeader>
              <TicketTitleRow>
                <TicketTitle>{ticket.title}</TicketTitle>
                <StatusBadge status={ticket.status}>
                  {TICKET_STATUS_LABELS[ticket.status]}
                </StatusBadge>
              </TicketTitleRow>
            </TicketHeader>
            
            <TicketMeta>
              <MetaItem>
                <MetaLabel>分类：</MetaLabel>
                {TICKET_CATEGORY_LABELS[ticket.category]}
              </MetaItem>
              <MetaItem>
                <MetaLabel>编号：</MetaLabel>
                #{ticket.ticket_id}
              </MetaItem>
              <MetaItem>
                <MetaLabel>创建时间：</MetaLabel>
                {formatDate(ticket.created_at)}
              </MetaItem>
            </TicketMeta>
          </TicketCard>
        ))}
      </TicketsList>
      
      {/* 分页组件 - 仅在总数量超过每页数量时显示 */}
      {totalCount > pageSize && (
        <PaginationContainer>
          <PaginationInfo>
            显示第 {getPaginationInfo().startItem} - {getPaginationInfo().endItem} 条，共 {totalCount} 条
          </PaginationInfo>
          <PaginationControls>
            <PaginationButton 
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!getPaginationInfo().hasPrev}
            >
              上一页
            </PaginationButton>
            
            <PageNumbers>
              {Array.from({ length: getPaginationInfo().totalPages }, (_, i) => i + 1).map(page => (
                <PageNumber
                  key={page}
                  onClick={() => onPageChange(page)}
                  active={page === currentPage}
                >
                  {page}
                </PageNumber>
              ))}
            </PageNumbers>
            
            <PaginationButton 
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!getPaginationInfo().hasNext}
            >
              下一页
            </PaginationButton>
          </PaginationControls>
        </PaginationContainer>
      )}
      
      {/* 工单详情弹窗 */}
      <TicketDetailModal
        ticketId={selectedTicketId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default TicketListComponent;

// 样式组件
const TicketsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TicketCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
  }
`;

const TicketHeader = styled.div`
  margin-bottom: 12px;
`;

const TicketTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TicketTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  line-height: 1.5;
  flex: 1;
`;

const TicketMeta = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const MetaItem = styled.div`
  font-size: 13px;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MetaLabel = styled.span`
  color: #a0aec0;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => TICKET_STATUS_COLORS[props.status] || '#718096'};
  white-space: nowrap;
`;

// 空状态
const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const EmptyTitle = styled.h3`
  color: #2d3748;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  color: #718096;
  font-size: 14px;
  margin: 0 0 24px 0;
`;

// 分页样式
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #718096;
  font-weight: 500;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #667eea;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #667eea;
    color: #4c51bf;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f7fafc;
    color: #a0aec0;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 4px;
  margin: 0 12px;
`;

const PageNumber = styled.button`
  width: 36px;
  height: 36px;
  background: ${props => props.active ? '#667eea' : 'white'};
  color: ${props => props.active ? 'white' : '#667eea'};
  border: 2px solid ${props => props.active ? '#667eea' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#5a67d8' : '#f8fafc'};
    border-color: #667eea;
    color: ${props => props.active ? 'white' : '#4c51bf'};
  }
`;
